import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { DemTypeahead, type DemTypeaheadOption } from '../../components/dem/DemTypeahead';
import { useAuth } from '../../contexts/AuthContext';
import {
  createBlanketOrder,
  setBlanketPoAttachments,
  uploadBlanketPoAttachment,
} from '../../lib/demBlanketOrders';
import { loadPoMappingContext } from '../../lib/demPoMapping';
import { getDemActorId } from '../../lib/demUserNames';
import { formatDemCustomerAddressDisplay } from '../../lib/demCustomers';
import type { DemCustomer } from '../../types/dem';

type LineDraft = {
  id: string;
  itemCode: string;
  description: string;
  packing: string;
  qty: string;
  uom: string;
};

function newLine(): LineDraft {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    itemCode: '',
    description: '',
    packing: '',
    qty: '',
    uom: 'PCS',
  };
}

function CollapseSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <section className={`delivery-form-collapse${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="delivery-form-collapse__toggle"
        aria-expanded={open}
        onClick={onToggle}
      >
        <span>{title}</span>
        <span aria-hidden>{open ? '▾' : '▸'}</span>
      </button>
      {open ? <div className="delivery-form-collapse__body">{children}</div> : null}
    </section>
  );
}

export function DemBlanketOrdersNewPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const actor = getDemActorId(profile);

  const [customers, setCustomers] = useState<DemCustomer[]>([]);
  const [itemOptions, setItemOptions] = useState<DemTypeaheadOption[]>([]);
  const [customerCode, setCustomerCode] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [lines, setLines] = useState<LineDraft[]>([newLine()]);
  const [headerOpen, setHeaderOpen] = useState(true);
  const [linesOpen, setLinesOpen] = useState(true);
  const [loadingCtx, setLoadingCtx] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingCtx(true);
    void loadPoMappingContext()
      .then((ctx) => {
        if (cancelled) return;
        setCustomers(ctx.customers);
        setItemOptions(
          ctx.itemOptions.map((item) => ({
            value: item.code,
            label: item.code,
            description: item.description,
            inventory: item.inventory,
            uom: item.uom,
            packing: item.packing,
            blocked: item.is_blocked,
          })),
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load customers/items.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCtx(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const customerOptions: DemTypeaheadOption[] = useMemo(
    () =>
      customers.map((c) => ({
        value: c.code,
        label: c.code,
        customerName: c.name,
        address: c.address,
        address2: c.address_2,
        postal: c.postal,
        blocked: Boolean(c.is_blocked),
      })),
    [customers],
  );

  const selectedCustomer = customers.find((c) => c.code === customerCode.trim()) ?? null;
  const customerName = selectedCustomer?.name ?? '';
  const customerAddress = selectedCustomer ? formatDemCustomerAddressDisplay(selectedCustomer) : '';

  const updateLine = (id: string, patch: Partial<LineDraft>) => {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, ...patch } : line)));
  };

  const commitItem = (lineId: string, code: string) => {
    const opt = itemOptions.find((o) => o.value === code);
    updateLine(lineId, {
      itemCode: code,
      description: opt?.description ?? '',
      packing: opt?.packing ?? '',
      uom: opt?.uom || 'PCS',
    });
  };

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await createBlanketOrder({
        customer_code: customerCode,
        customer_name: selectedCustomer?.name ?? '',
        po_number: poNumber,
        notes,
        created_by: actor,
        lines: lines.map((line) => ({
          item_code: line.itemCode,
          description: line.description,
          ordered_qty: Number(line.qty),
          uom: line.uom,
          unit_price: null,
        })),
      });
      if (pendingFiles.length > 0) {
        const uploaded = [];
        for (const file of pendingFiles) {
          uploaded.push(await uploadBlanketPoAttachment(created.id, file));
        }
        await setBlanketPoAttachments(created.id, uploaded);
      }
      navigate(`/dem/blanket-orders/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save blanket order.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="delivery-page delivery-page--form delivery-page--floating">
      <Link to="/dem/blanket-orders" className="btn-secondary delivery-page__back">
        Back to list
      </Link>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {loadingCtx ? <div className="alert alert-info">Loading customers and items…</div> : null}

      <div className="dem-blanket-form">
        <CollapseSection title="Header" open={headerOpen} onToggle={() => setHeaderOpen((v) => !v)}>
          <div className="dem-blanket-form__row dem-blanket-form__row--2">
            <label className="field">
              <span className="field-label">Customer no. *</span>
              <DemTypeahead
                value={customerCode}
                onChange={setCustomerCode}
                onCommit={setCustomerCode}
                options={customerOptions}
                placeholder="Search customer code or name"
                tableMode="customer"
                disabled={loadingCtx || saving}
                inputClassName="field-input"
              />
            </label>
            <label className="field">
              <span className="field-label">Customer name</span>
              <input
                className="field-input dem-blanket-form__readonly"
                value={customerName}
                readOnly
                tabIndex={-1}
                placeholder="—"
              />
            </label>
          </div>

          <label className="field">
            <span className="field-label">Address</span>
            <textarea
              className="field-input dem-blanket-form__readonly"
              rows={2}
              value={customerAddress}
              readOnly
              tabIndex={-1}
              placeholder="—"
            />
          </label>

          <div className="dem-blanket-form__row dem-blanket-form__row--po-notes">
            <label className="field">
              <span className="field-label">PO number *</span>
              <input
                className="field-input"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder="e.g. 7566007321"
                disabled={saving}
              />
            </label>
            <label className="field">
              <span className="field-label">Notes</span>
              <textarea
                className="field-input"
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — e.g. Endoscopy Centre, blanket estimate"
                disabled={saving}
              />
            </label>
          </div>

          <div className="dem-blanket-form__attachments">
            <span className="field-label">PO attachment</span>
            <p className="delivery-form-card__hint">
              Optional PDF or image of the customer PO. You can open it from the blanket detail page.
            </p>
            <label className="btn-secondary btn-sm dem-blanket-form__upload-btn">
              Add files
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                hidden
                disabled={saving}
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : [];
                  if (files.length) setPendingFiles((prev) => [...prev, ...files]);
                  e.target.value = '';
                }}
              />
            </label>
            {pendingFiles.length > 0 ? (
              <ul className="dem-blanket-form__file-list">
                {pendingFiles.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <span>{file.name}</span>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={saving}
                      onClick={() =>
                        setPendingFiles((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="delivery-form-card__hint">No files selected.</p>
            )}
          </div>
        </CollapseSection>

        <CollapseSection title="Lines" open={linesOpen} onToggle={() => setLinesOpen((v) => !v)}>
          <div className="dem-blanket-form__lines-toolbar">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={saving}
              onClick={() => setLines((prev) => [...prev, newLine()])}
            >
              Add line
            </button>
          </div>

          <div className="dem-blanket-lines">
            {lines.map((line, index) => (
              <div key={line.id} className="dem-blanket-lines__row">
                <span className="dem-blanket-lines__idx">{index + 1}</span>
                <label className="field">
                  <span className="field-label">Item *</span>
                  <DemTypeahead
                    value={line.itemCode}
                    onChange={(code) =>
                      updateLine(line.id, {
                        itemCode: code,
                        ...(code.trim()
                          ? {}
                          : { description: '', packing: '', uom: 'PCS' }),
                      })
                    }
                    onCommit={(code) => commitItem(line.id, code)}
                    options={itemOptions}
                    placeholder="Item code"
                    tableMode="item"
                    disabled={loadingCtx || saving}
                    inputClassName="field-input"
                  />
                </label>
                <label className="field dem-blanket-lines__desc">
                  <span className="field-label">Description</span>
                  <input
                    className="field-input dem-blanket-form__readonly"
                    value={line.description}
                    readOnly
                    tabIndex={-1}
                    placeholder="—"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Packing size</span>
                  <input
                    className="field-input dem-blanket-form__readonly"
                    value={line.packing}
                    readOnly
                    tabIndex={-1}
                    placeholder="—"
                  />
                </label>
                <label className="field">
                  <span className="field-label">Qty *</span>
                  <input
                    className="field-input"
                    type="number"
                    min={0}
                    step="any"
                    value={line.qty}
                    onChange={(e) => updateLine(line.id, { qty: e.target.value })}
                    disabled={saving}
                  />
                </label>
                <label className="field">
                  <span className="field-label">UOM</span>
                  <input
                    className="field-input dem-blanket-form__readonly"
                    value={line.uom}
                    readOnly
                    tabIndex={-1}
                  />
                </label>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  disabled={saving || lines.length <= 1}
                  onClick={() => setLines((prev) => prev.filter((l) => l.id !== line.id))}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </CollapseSection>

        <div className="dem-blanket-form__actions">
          <Link to="/dem/blanket-orders" className="btn-secondary">
            Cancel
          </Link>
          <button
            type="button"
            className="btn-primary"
            disabled={saving || loadingCtx}
            onClick={() => void onSave()}
          >
            {saving ? 'Saving…' : 'Save blanket'}
          </button>
        </div>
      </div>
    </div>
  );
}
